import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@powersync/react';
import { useApp, useClinicCtx } from '@/app/state';
import { Button, Card, Empty, Field, Input, Modal, Pill, cx } from '@/components/ui';
import { dispense, dispensingQueue, prescriptionLines, type PendingRx, type RxLine } from '@/features/pharmacy/repo';
import { allocateFefo, batchesForDrug, daysToExpiry, type BatchStock } from '@/features/pharmacy/stock';
import { MedicineLabels, type LabelItem } from '@/features/pharmacy/MedicineLabel';
import { formatDate, formatMMK, toMyanmarDigits } from '@/lib/myanmar/digits';

interface Line extends RxLine {
  give: number;             // how much to hand over now
  stock: BatchStock[];
  onHand: number;
  shortfall: number;
  nearestExpiry: string | null;
}

export function DispensePage() {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const ctx = useClinicCtx();
  const { clinic } = useApp();
  const [queue, setQueue] = useState<PendingRx[]>([]);
  const [selected, setSelected] = useState<PendingRx | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState(false);
  const [labels, setLabels] = useState<{ items: LabelItem[]; patientName: string; patientNo: string | null } | null>(null);

  const { data: tick } = useQuery('select count(*) as n, max(updated_at) as u from prescriptions where clinic_id = ?', [ctx.clinicId]);
  useEffect(() => { void dispensingQueue(ctx.db, ctx.clinicId).then(setQueue); }, [ctx.db, ctx.clinicId, tick]);

  const load = useCallback(async (rx: PendingRx) => {
    const raw = await prescriptionLines(ctx.db, rx.prescription_id);
    const withStock: Line[] = [];
    for (const l of raw) {
      const stock = l.drug_id ? await batchesForDrug(ctx.db, l.drug_id) : [];
      const outstanding = Math.max(l.quantity - l.already, 0);
      const alloc = allocateFefo(stock, outstanding);
      const onHand = stock.filter((b) => daysToExpiry(b.expiry_on) === null || daysToExpiry(b.expiry_on)! >= 0).reduce((s, b) => s + b.qty_on_hand, 0);
      withStock.push({
        ...l, stock, onHand, shortfall: alloc.shortfall,
        nearestExpiry: alloc.allocations[0]?.expiry_on ?? null,
        give: l.buy_outside === 1 ? 0 : alloc.allocated,
      });
    }
    setSelected(rx);
    setLines(withStock);
  }, [ctx.db]);

  async function handOver() {
    if (!selected) return;
    setBusy(true);
    try {
      const toGive = lines.filter((l) => l.give > 0 && l.drug_id);
      await dispense(ctx, {
        prescription_id: selected.prescription_id,
        patient_id: selected.patient_id,
        lines: toGive.map((l) => ({ prescription_item_id: l.id, drug_id: l.drug_id!, qty: l.give, unit_price: l.selling_price ?? 0 })),
      });
      setLabels({
        items: toGive.map((l) => ({
          drug_name: l.drug_name, qty: l.give, dose_qty: l.dose_qty, dose_unit: l.dose_unit,
          frequency_code: l.frequency_code, instruction_codes: JSON.parse(l.instruction_codes ?? '[]') as string[],
        })),
        patientName: name(selected),
        patientNo: selected.patient_no,
      });
      setSelected(null); setLines([]);
    } finally { setBusy(false); }
  }

  const name = (r: PendingRx) => `${r.honorific && r.honorific !== 'none' ? t(`honorific.${r.honorific}`) + ' ' : ''}${my ? (r.name_my ?? r.name_en ?? '') : (r.name_en ?? r.name_my ?? '')}`;
  const total = lines.reduce((s, l) => s + l.give * (l.selling_price ?? 0), 0);

  return (
    <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
      <div className="grid gap-2 self-start">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">{t('pharmacy.queue')}</h2>
        {queue.length === 0 && <Empty>{t('pharmacy.noPending')}</Empty>}
        {queue.map((r) => (
          <button key={r.prescription_id} onClick={() => void load(r)}
            className={cx('rounded-xl border p-3 text-left', selected?.prescription_id === r.prescription_id ? 'border-accent bg-accent-soft' : 'border-rule bg-surface hover:bg-accent-soft/40')}>
            <div className="font-bold">{name(r)}</div>
            <div className="text-sm text-ink-2">
              {r.patient_no} · {r.item_count} {my ? 'မျိုး' : 'items'}
              {r.status === 'partially_dispensed' && <Pill tone="warn"> {t('pharmacy.alreadyGiven', { n: '' })}</Pill>}
            </div>
          </button>
        ))}
      </div>

      {!selected ? <Empty>{t('pharmacy.nothingSelected')}</Empty> : (
        <Card className="grid gap-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h1 className="text-xl font-bold">{name(selected)}</h1>
            <span className="text-sm text-ink-2">{selected.patient_no} · {my ? (selected.doctor_name_my ?? selected.doctor_name_en) : (selected.doctor_name_en ?? selected.doctor_name_my)}</span>
          </div>

          <ul className="divide-y divide-rule">
            {lines.map((l, i) => {
              const outstanding = Math.max(l.quantity - l.already, 0);
              const days = daysToExpiry(l.nearestExpiry);
              return (
                <li key={l.id} className="grid gap-1 py-3">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold">{l.drug_name}</span>
                    <span className="text-sm text-ink-2">{formatMMK(l.selling_price ?? 0, my ? 'my' : 'en')} × {my ? toMyanmarDigits(l.give) : l.give}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-ink-2">
                    <span>{t('pharmacy.prescribed')}: {my ? toMyanmarDigits(l.quantity) : l.quantity}</span>
                    {l.already > 0 && <Pill>{t('pharmacy.alreadyGiven', { n: my ? toMyanmarDigits(l.already) : l.already })}</Pill>}
                    {l.buy_outside === 1 ? <Pill tone="warn">{t('pharmacy.buyOutside')}</Pill> : (
                      <>
                        <span>{t('pharmacy.onHand')}: {my ? toMyanmarDigits(l.onHand) : l.onHand}</span>
                        {l.shortfall > 0 && <Pill tone="danger">{t('pharmacy.shortBy', { n: my ? toMyanmarDigits(l.shortfall) : l.shortfall })}</Pill>}
                        {days !== null && days <= 30 && <Pill tone="warn">{t('pharmacy.expires', { date: formatDate(l.nearestExpiry, my ? 'my' : 'en') })}</Pill>}
                      </>
                    )}
                    <label className="ml-auto flex items-center gap-2">
                      <span>{t('pharmacy.given')}</span>
                      <Input type="number" min={0} max={outstanding} inputMode="numeric" className="w-24 text-center font-bold"
                        value={l.give}
                        onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, give: Math.max(0, Math.min(Number(e.target.value) || 0, outstanding)) } : x))} />
                    </label>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-rule pt-3">
            <span className="text-lg font-bold">{t('pharmacy.total')}: {formatMMK(total, my ? 'my' : 'en')}</span>
            <Button variant="primary" className="text-lg" disabled={busy || lines.every((l) => l.give <= 0)} onClick={handOver}>
              {t('pharmacy.dispenseSelected')}
            </Button>
          </div>
        </Card>
      )}

      <Modal open={!!labels} onClose={() => setLabels(null)} title={t('pharmacy.label')} wide>
        {labels && (
          <MedicineLabels
            items={labels.items} patientName={labels.patientName} patientNo={labels.patientNo}
            clinicName={(my && clinic?.name_my) || clinic?.name || ''}
            onClose={() => setLabels(null)}
          />
        )}
      </Modal>
    </div>
  );
}
